import {useCallback, useMemo, useState} from 'react';
import type {Chart, TooltipModel} from 'chart.js';

type BarTooltip = {
    chart: Chart<'bar'>;
    dataIndex: number;
    x: number;
    y: number;
};

export const useBarChartTooltip = () => {
    const [activeTooltip, setActiveTooltip] = useState<BarTooltip | null>(null);
    const externalTooltip = useCallback(
        ({chart, tooltip}: {chart: Chart<'bar'>; tooltip: TooltipModel<'bar'>}) => {
            const point = tooltip.dataPoints?.[0];
            if (tooltip.opacity === 0 || !point) {
                setActiveTooltip(null);
                return;
            }

            const next = {
                chart,
                dataIndex: point.dataIndex,
                x: tooltip.caretX,
                y: tooltip.caretY,
            };
            setActiveTooltip(current =>
                current?.chart === next.chart &&
                current.dataIndex === next.dataIndex &&
                current.x === next.x &&
                current.y === next.y
                    ? current
                    : next
            );
        },
        []
    );
    const tooltipTarget = useMemo(
        () => activeTooltip && {
            getBoundingClientRect: () => {
                const {chart, x, y} = activeTooltip;
                const rect = chart.canvas.getBoundingClientRect();
                return new DOMRect(
                    rect.left + (x * rect.width) / chart.width,
                    rect.top + (y * rect.height) / chart.height,
                    0,
                    0
                );
            },
        },
        [activeTooltip]
    );

    return {activeTooltip, externalTooltip, tooltipTarget};
};
